var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useLayoutEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { BlockEngineApiContext } from './BlockEngineApi.js';
import { BlockEngineApiFrontend } from './BlockEngineApiFrontend.js';
import { BlockViewer } from './BlockViewer.js';
import { camelCaseMap } from './camelCasedAttributes.js';
import { getBlock } from './registerBlock.js';
const noteEnoughCandidates = new Error('getCommonAncestor: not enough parameters');
function getCommonAncestor(node1, ...nodes) {
    if (arguments.length < 2) {
        throw noteEnoughCandidates;
    }
    let commonAncestor = node1, found = nodes.length === 0;
    do {
        for (let j = 0; j < nodes.length; j++) {
            if (!commonAncestor.contains(nodes[j])) {
                let parentNode = commonAncestor.parentNode;
                if (parentNode != null) {
                    commonAncestor = parentNode;
                    break;
                }
                else {
                    return null;
                }
            }
            if (j == nodes.length - 1) {
                found = true;
            }
        }
    } while (!found);
    return commonAncestor;
}
function createSmartfireDOMElement(node, children) {
    let elementProps = {};
    if (node.attributes) {
        for (let i = 0; i < node.attributes.length; i++) {
            const attribute = node.attributes.item(i);
            if (!attribute) {
                continue;
            }
            let key = attribute.name;
            let value = attribute.value;
            key = camelCaseMap[key.replace(/[-:]/, '')] || key;
            if (key === 'style') {
                continue;
            }
            else if (key === 'class') {
                continue;
            }
            else if (key === 'for') {
                key = 'htmlFor';
            }
            elementProps[key] = value;
        }
    }
    if (node.className) {
        elementProps['className'] = node.className;
    }
    if (node.style) {
        let style = undefined;
        for (let i = 0; i < node.style.length; i++) {
            let prop = node.style[i];
            if (node.style[prop]) {
                if (!style) {
                    style = {};
                }
                style[prop] = node.style[prop];
            }
        }
        if (style) {
            elementProps['style'] = style;
        }
    }
    return [node.tagName.toLowerCase(), elementProps, children];
}
function mapNodesToSmartfireTreeArray(node) {
    return Array.from(node.childNodes).map(mapNodeToSmartfireTree)
        .filter(c => c !== null);
}
function mapNodeToSmartfireTree(node) {
    const children = mapNodesToSmartfireTreeArray(node);
    if (node.nodeType === Node.ELEMENT_NODE) {
        const currentNode = node;
        if (currentNode.tagName.toLowerCase() === 'smartfire-component') {
            const componentName = currentNode.getAttribute('name');
            if (componentName == null) {
                return null;
            }
            const attributesRaw = currentNode.getAttribute('attributes');
            let attributes;
            if (attributesRaw == null) {
                attributes = {};
            }
            else {
                attributes = JSON.parse(attributesRaw);
            }
            return ['scm:' + componentName, Object.assign({}, attributes), children];
        }
        if (children != null && children.length > 0)
            return createSmartfireDOMElement(currentNode, children);
        else {
            return createSmartfireDOMElement(currentNode);
        }
    }
    else if (node.nodeType === Node.TEXT_NODE) {
        let content = node.wholeText;
        if (content.trim().length <= 0) {
            return null;
        }
        return content;
    }
    else {
        return null;
    }
}
function mapSmartfireNodesToReactElements(nodes) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!nodes) {
            return undefined;
        }
        return (yield Promise.all(nodes.map((child, index) => mapSmartfireNodeToReactElement(child, index))))
            .filter(c => c !== null);
    });
}
function mapSmartfireNodeToReactElement(node, index) {
    return __awaiter(this, void 0, void 0, function* () {
        if (typeof node === 'string') {
            return node;
        }
        if (Array.isArray(node)) {
            const [type, props, childrenNodes] = node;
            props.key = index;
            const children = yield mapSmartfireNodesToReactElements(childrenNodes);
            if (type.startsWith('scm:')) {
                const [/* unused */ , componentType] = type.split(':', 2);
                const Viewer = BlockViewer({
                    block: yield getBlock(componentType),
                    children,
                });
                return React.createElement(Viewer, props);
            }
            else {
                return React.createElement(type, props, children);
            }
        }
        else {
            return null;
        }
    });
}
function generateAppFromTree(smartfireAppTree, mainNode) {
    return __awaiter(this, void 0, void 0, function* () {
        const reactNodes = yield mapSmartfireNodesToReactElements(smartfireAppTree);
        function Application() {
            const frontendContext = useMemo(() => new BlockEngineApiFrontend(), []);
            useLayoutEffect(() => {
                mainNode.__smartfire = {
                    tree: smartfireAppTree,
                    renderedHTML: mainNode.innerHTML,
                };
                console.log('Mounted Smartfire Block Tree on ', mainNode);
            }, []);
            return React.createElement(React.StrictMode, {}, React.createElement(BlockEngineApiContext.Provider, {
                value: frontendContext
            }, reactNodes));
        }
        return {
            Application,
            mount: mainNode,
            tree: smartfireAppTree,
        };
    });
}
export function generateReactApp() {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const nodes = Array.from(document.querySelectorAll('smartfire-component'));
        let mainNode = getCommonAncestor(nodes[0], ...nodes);
        if (mainNode.tagName.toLowerCase() === 'smartfire-component') {
            mainNode = ((_a = mainNode.parentElement) !== null && _a !== void 0 ? _a : mainNode);
        }
        mainNode.classList.add('react-main-app-node');
        mainNode.classList.add(renderingMainNodeClass);
        const smartfireAppTree = mapNodesToSmartfireTreeArray(mainNode);
        if (smartfireAppTree === null) {
            throw new Error("Unable to generate an App tree");
        }
        return yield generateAppFromTree(smartfireAppTree, mainNode);
    });
}
const renderingMainNodeClass = 'smartfire-block-react-app-main';
const dataPreRenderTree = "data-" + renderingMainNodeClass;
function renderPreRenderPage() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!window.smartfireWEXTDataGet) {
            throw new Error('We are not in a WEXT Server pre-render system');
        }
        const preRenderConfig = yield window.smartfireWEXTDataGet();
        let app = yield simpleRenderFullPage();
        if (!preRenderConfig.staticRender && app) {
            ReactDOM.unmountComponentAtNode(app.mount);
            app.mount.innerHTML = app.mount.__smartfire.renderedHTML;
            app.mount.setAttribute(dataPreRenderTree, JSON.stringify(app.mount.__smartfire.tree));
        }
    });
}
function simpleRenderFullPage() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const ReactApp = yield generateReactApp();
            ReactDOM.render(React.createElement(ReactApp.Application), ReactApp.mount);
            return ReactApp;
        }
        catch (e) {
            if (e === noteEnoughCandidates) {
                console.log("No Smartfire content to render on this page");
                return undefined;
            }
            else {
                throw e;
            }
        }
    });
}
function restorePageApp(treeData, preRenderDOMElement) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const tree = JSON.parse(treeData);
            const ReactApp = yield generateAppFromTree(tree, preRenderDOMElement);
            preRenderDOMElement.removeAttribute(dataPreRenderTree);
            ReactDOM.hydrate(React.createElement(ReactApp.Application), ReactApp.mount);
            return;
        }
        catch (e) {
            console.error('Fatal error while trying to restore pre-render', e);
            throw e;
        }
    });
}
/**
 * @deprecated Prefer to use the SmartfireBlockPageRenderer class
 */
export function renderPage() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            if (window.smartfireWEXTDataGet) {
                yield renderPreRenderPage();
            }
            else {
                let preRenderDOM = document.getElementsByClassName(renderingMainNodeClass);
                if (preRenderDOM.length > 0) {
                    let preRenderDOMElement = preRenderDOM[0];
                    let rawTreeData = preRenderDOMElement.getAttribute(dataPreRenderTree);
                    if (rawTreeData) {
                        yield restorePageApp(rawTreeData, preRenderDOMElement);
                        return;
                    }
                }
                yield simpleRenderFullPage();
            }
        }
        catch (e) {
            console.error("Unable to mount a Smartfire Block application due to underlying error", e);
        }
    });
}
try {
    customElements.define('smartfire-component', class extends HTMLElement {
        constructor() {
            super();
            try {
                let node = document.createElement('div');
                node.style.display = 'none';
                node.innerHTML = `<slot></slot>`;
                this.attachShadow({ mode: 'open' }).appendChild(node);
            }
            catch (e) {
                console.error('Error occurred while rendering the custom element', e);
            }
        }
    });
}
catch (e) {
    console.error('Your browser does not seems to support custom-elements\nPlease upgrade your browser.');
}

//# sourceMappingURL=renderPage.js.map
